import { HttpErrorResponse } from "@angular/common/http";
import { NgIf } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
} from "@angular/core";
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { SnackbarService } from "@fuse/services/snackbar";
import { TranslocoModule, TranslocoService } from "@jsverse/transloco";
import { AuthService } from "app/core/auth/auth.service";
import { translateApiError } from "app/shared/utils/api-error";
import { Subject, takeUntil } from "rxjs";
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, confirmPasswordError, newPasswordError } from "./change-password.rules";

type PasswordField = "currentPassword" | "password" | "confirmPassword";

@Component({
  selector: "app-change-password-modal",
  templateUrl: "./change-password-modal.component.html",
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    TranslocoModule,
  ],
})
export class ChangePasswordModalComponent implements OnInit, OnDestroy {
  readonly minLength = PASSWORD_MIN_LENGTH;
  readonly maxLength = PASSWORD_MAX_LENGTH;

  saving = false;
  visible: Record<PasswordField, boolean> = { currentPassword: false, password: false, confirmPassword: false };

  private _authService = inject(AuthService);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _dialogRef: MatDialogRef<ChangePasswordModalComponent, boolean> = inject(MatDialogRef);
  private _snackbarService = inject(SnackbarService);
  private _translocoService = inject(TranslocoService);
  private _unsubscribeAll = new Subject<void>();

  form = inject(FormBuilder).nonNullable.group({
    currentPassword: ["", Validators.required],
    password: ["", (control: AbstractControl) => this._newPasswordValidator(control)],
    confirmPassword: ["", (control: AbstractControl) => this._confirmPasswordValidator(control)],
  });

  ngOnInit(): void {
    // As regras dependem do campo vizinho: revalida quando ele muda.
    this.form.controls.currentPassword.valueChanges
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => this.form.controls.password.updateValueAndValidity());

    this.form.controls.password.valueChanges
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => this.form.controls.confirmPassword.updateValueAndValidity());
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  toggleVisible(field: PasswordField): void {
    this.visible[field] = !this.visible[field];
  }

  submit(): void {
    if (this.saving) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { currentPassword, password } = this.form.getRawValue();

    this.saving = true;
    this._dialogRef.disableClose = true;
    this.form.disable({ emitEvent: false });
    this._changeDetectorRef.markForCheck();

    this._authService.changePassword(currentPassword, password).subscribe({
      next: () => {
        this._snackbarService.open(this._translocoService.translate("user.change_password.success"), "success");
        this._dialogRef.close(true);
      },
      error: (e: unknown) => this._handleError(e),
    });
  }

  private _handleError(e: unknown): void {
    this.saving = false;
    this._dialogRef.disableClose = false;
    this.form.enable({ emitEvent: false });

    const status = e instanceof HttpErrorResponse ? e.status : 0;
    const code = (e as { error?: { error?: unknown } } | null)?.error?.error;

    if (status === 401) {
      // O authInterceptor já encerra a sessão e recarrega a aplicação.
      return;
    }

    if (code === "InvalidPassword") {
      const control = this.form.controls.currentPassword;
      control.setErrors({ invalidPassword: true });
      control.markAsTouched();
    } else if (status === 429) {
      this._snackbarService.open(this._translocoService.translate("user.change_password.too_many_requests"), "error");
    } else {
      this._snackbarService.open(translateApiError((key) => this._translocoService.translate(key), e), "error");
    }

    this._changeDetectorRef.markForCheck();
  }

  private _newPasswordValidator(control: AbstractControl): ValidationErrors | null {
    const currentPassword = control.parent?.get("currentPassword")?.value ?? "";
    const error = newPasswordError(control.value ?? "", currentPassword);

    return error ? { [error]: true } : null;
  }

  private _confirmPasswordValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.parent?.get("password")?.value ?? "";
    const error = confirmPasswordError(control.value ?? "", password);

    return error ? { [error]: true } : null;
  }
}
